import {BehaviorSubject, combineLatest, Subject, switchMap, of} from 'rxjs';
import {debounceTime, filter, map, shareReplay, startWith} from 'rxjs/operators';
import {LeverageDisplayMode} from '@px/enum/leverage';
import {PositionMode} from '@px/enum';
import {contract$} from '@px/streams/contract';
import {positions$} from '@px/streams/position';
import {contractBiz$, updateCustomize} from './contract-biz';
import {isLogin$} from '@px/streams/auth';
import {PriceType} from '@px/enum/perpetual';
import {wwData$} from '@px/streams/worker-data';

import PositionsData = ww.PositionsData;
import Msg = ww.Msg;

const BIZ_KEY = 'positionSetting';

export interface PositionSetting {
  leverageDisplayMode: LeverageDisplayMode;
  priceSelectionBasis: PriceType;
}

export const positionMode$ = new BehaviorSubject<PositionMode>(PositionMode.Hedge);
export const leverageDisplayMode$ = new BehaviorSubject<LeverageDisplayMode>(LeverageDisplayMode.Position);
export const customizeUpdatePosSetting$ = new Subject<void>();
export const posSettings$ = new BehaviorSubject<PositionSetting>(initPosSetting());
const version$ = new BehaviorSubject<number>(1);

export const curPositions$ = combineLatest([contract$, positions$]).pipe(
  filter(([_, positions]) => positions && positions.length > 0),
  map(([contract, positions]) => positions.filter((p) => p.symbol === contract.symbol)),
  filter(Boolean),
  startWith([]),
  shareReplay<Position[]>(1)
);

export const airdropPositions$ = new BehaviorSubject<Position[]>([]);
wwData$
  .pipe(
    filter((o: Msg<PositionsData>) => o.type === 'airdrop-positions'),
    map((msg) => msg.data.positions)
  )
  .subscribe((positions) => {
    if (positions.length < 1) {
      airdropPositions$.next([]);
      return;
    }
    airdropPositions$.next(positions);
  });

curPositions$
  .pipe(
    switchMap((positions) => {
      let condPositions;
      // 没有 ws 推送的仓位，新仓位的 position mode 默认 follow major Symbol (BTCUSDT)
      if (positions.every((p) => p.accountID === 0)) {
        condPositions = (positions$.getValue() || []).filter((p) => p.symbol === 'BTCUSDT');
      } else {
        condPositions = positions;
      }
      return of(condPositions.filter(({posMode}) => !!posMode).every(({posMode}) => posMode === PositionMode.Hedge));
    })
  )
  .subscribe((isHedged) => positionMode$.next(isHedged ? PositionMode.Hedge : PositionMode.OneWay));

function initPosSetting() {
  return {
    leverageDisplayMode: LeverageDisplayMode.Position,
    priceSelectionBasis: PriceType.Last,
  };
}

contractBiz$.subscribe((items) => {
  const item = items.find((item) => item.bizKey === BIZ_KEY);
  if (item) {
    const posSetting = {...initPosSetting(), ...JSON.parse(item.bizVal)};
    posSettings$.next(posSetting);
    version$.next(item.version);
    leverageDisplayMode$.next(posSetting.leverageDisplayMode);
  }
});

combineLatest([isLogin$, posSettings$, customizeUpdatePosSetting$])
  .pipe(
    debounceTime(1000),
    filter(([isLogin]) => isLogin),
    switchMap(([_, settings]) => {
      const curVersion = version$.getValue();
      return updateCustomize({bizKey: BIZ_KEY, curVersion, settings});
    })
  )
  .subscribe((version: number) => version$.next(version));
